/* eslint-disable max-lines-per-function */
import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon20InfoCircleOutline } from '@vkontakte/icons';
import { classNames, Spacing, Switch } from '@vkontakte/vkui';
import theme from '@vkontakte/vkui-tokens/themes/cloud/cssVars/theme';
import familyByOptionImage from 'img/family-by-option/doc.png';
import { xray } from 'lib/xray';
import throttle from 'lodash.throttle';
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { sendStartEventTimer } from 'reactApp/appHelpers/featuresHelpers';
import { multiTariffHashTariffAnalytics, multiTariffSendTariffAnalytics } from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import {
    familyByOptionsEnterTooltipFeatureMobile,
    familyByOptionsFeatureMobile,
    multiTariffFamilyTariffListMobile,
    multiTariffsMobileTabsAnimation,
    multiTariffTouchRegularOrAnonymous,
    multiTariffVerticalMobile,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import { promocodeInTariffsMobile } from 'reactApp/appHelpers/featuresHelpers/features/promocodeOnPromoQuota';
import {
    quotaLandingLoadProductsThresholdMobile,
    quotaLandingLoadProductsTimeout,
} from 'reactApp/appHelpers/featuresHelpers/features/quotaLandingLoadProductsTimeouts';
import { START_EVENT_TIMER_MS } from 'reactApp/constants';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import {
    changeMultiTariffMobileActiveTabAction,
    loadQuotaLandingProducts,
    showLandingTariffsAction,
} from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { getMultiTariffMobileActiveTab } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { useIntersecObsrWithParams } from 'reactApp/sections/FamilyLanding/hooks/useIntersecObsrWithParams';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { FamilyByOptionTooltip } from 'reactApp/sections/QuotaLanding/FamilyByOptionTooltip/FamilyByOptionTooltip';
import { useFamilyByOptionInfoTooltip } from 'reactApp/sections/QuotaLanding/FamilyByOptionTooltip/FamilyByOptionTooltip.hook';
import { sendMultiTariffHashAnalytics } from 'reactApp/sections/QuotaLanding/MultiTariffsList/MultiTariffsList.helpers';
import { BuyButtons } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/BuyButtons';
import { Features } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/Features/Features';
import { useAutoShowTooltip } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/Features/hooks/useAutoShowTooltip';
import { useGetProductsSpaces } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/hooks/useGetProductsSpaces';
import { useTabOptions } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/hooks/useTabOptions';
import { MultiTariffCardLoader } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffCardLoader/MultiTariffCardLoader';
import { getMultiTariffsListMobileIds } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffListMobile.helpers';
import styles from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffsListMobile.css';
import { MultiTariffsListMobileSpaceCard } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffsListMobileSpaceCard';
import { MultiTariffsTabsMobile } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffsTabsMobile/MultiTariffsTabsMobile';
import { VerticalTariffCardList } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/VerticalTariffCardList/VerticalTariffCardList';
import { openPromocodeModal, sendPromocodeButtonAnalytics } from 'reactApp/sections/QuotaLanding/PromocodeDialog/PromocodeDialog.helpers';
import { ETabs } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { sendQuotaBlockViewDWH, sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { useMobileBuyClick } from 'reactApp/sections/QuotaLanding/TariffsList/useMobileBuyClick';
import { useBuyClick } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/useBuyClick';
import type { RootState } from 'reactApp/store';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { usePriceDif } from 'reactApp/ui/TariffSectionNew/hooks/usePriceDif';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

interface MultiTariffsListMobileProps {
    changeTab?: (tab) => void;
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isAnonymous: boolean;
    headerHeight?: number;
    isTariffsPage?: boolean;
    isMobilePayAvailable: boolean;
    isMobilePayFromGetParam?: boolean;
    mayPromo2025Enabled?: boolean;
    blackFriday2025Enabled?: boolean;
}

const FAMILY_ENTER_TOOLTIP_ID = 'family-enter-tooltip';

export const MultiTariffsListMobile = memo(
    forwardRef<HTMLDivElement | null, MultiTariffsListMobileProps>(
        (
            {
                headerHeight = 0,
                isTariffsPage,
                isMobilePayAvailable,
                isMobilePayFromGetParam = false,
                isIOSPromo,
                isAndroidPromo,
                mayPromo2025Enabled,
                changeTab,
                blackFriday2025Enabled,
            },
            ref
        ) => {
            const dispatch = useDispatch();
            const s = styles as Record<string, string>;

            const { infoTooltipRef } = useFamilyByOptionInfoTooltip();
            const [familyByOptionsSwitchState, setFamilyByOptionsSwitchState] = useState(true);
            const firstLoad = useRef<boolean>(false);

            const tabOptions = useTabOptions();

            const activeTariffTab = useSelector(getMultiTariffMobileActiveTab);

            const withActionTariff = useMemo(() => blackFriday2025Enabled && activeTariffTab === ETabsName.advanced, [activeTariffTab]);

            const { showTooltip, setShowTooltip } = useAutoShowTooltip(
                familyByOptionsEnterTooltipFeatureMobile,
                FAMILY_ENTER_TOOLTIP_ID,
                activeTariffTab,
                ETabsName.advanced,
                1000
            );

            const swiperRef = useRef<SwiperInstance | null>(null);
            const productsLoadTimer = useRef<NodeJS.Timeout | null>(null);

            const { isLoading, isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);

            const isAlmostFullFreeUser = useSelector(UserSelectors.isAlmostFullFreeUser);

            const { spaces, products, selectorDataLoaded = false } = useGetProductsSpaces();
            const familyByOptionList = useSelector((state: RootState) => {
                return ProductsSelectors.getNormalizedProductsBySpace(state, [
                    ...multiTariffFamilyTariffListMobile.year,
                    ...multiTariffFamilyTariffListMobile.month,
                ]);
            });
            const [currentSpace, setCurrentSpace] = useState<number>(0);

            const familyTariffForCurrentSpace = familyByOptionList?.[currentSpace]
                ? {
                      space: { original: currentSpace },
                      tariffId: `family-by-options-${currentSpace}`,
                      products: familyByOptionList[currentSpace],
                  }
                : undefined;

            const currentProducts = products?.[currentSpace];

            const getShadowTariff = familyByOptionsSwitchState && familyByOptionsFeatureMobile && activeTariffTab === ETabsName.advanced;
            const { monthProduct, yearProduct } =
                getShadowTariff && familyTariffForCurrentSpace?.products ? familyTariffForCurrentSpace.products : currentProducts || {};

            const familyByOptionsHandler = useCallback(() => {
                const wasOn = familyByOptionsSwitchState;
                const fromProducts =
                    wasOn && familyTariffForCurrentSpace?.products ? familyTariffForCurrentSpace.products : currentProducts;
                const toProducts = !wasOn && familyTariffForCurrentSpace?.products ? familyTariffForCurrentSpace.products : currentProducts;

                sendQuotaGa({
                    action: 'family_features_switcher',
                    mode: wasOn ? 'off' : 'on',
                    tariff_from: fromProducts?.yearProduct?.id,
                    tariff_to: toProducts?.yearProduct?.id,
                });

                setFamilyByOptionsSwitchState(!wasOn);
            }, [familyByOptionsSwitchState, familyTariffForCurrentSpace, currentProducts]);

            const showSpecialFamilyCard = activeTariffTab === ETabsName.family && spaces.length === 1;

            const tariffPriceDif = usePriceDif({
                products: currentProducts
                    ? {
                          yearProduct: currentProducts.yearProduct!,
                          monthProduct: currentProducts.monthProduct,
                      }
                    : undefined,
                familyProducts: familyTariffForCurrentSpace?.products
                    ? {
                          yearProduct: familyTariffForCurrentSpace.products.yearProduct!,
                          monthProduct: familyTariffForCurrentSpace.products.monthProduct,
                      }
                    : undefined,
                activeTab: ETabsName.year,
            });

            const onBuyClick = useBuyClick({
                isMobilePayAvailable,
                isMobilePayFromGetParam,
                isIOSPromo,
                isAndroidPromo,
                enabledMidasPayment: true,
            });

            const onMobileBuyClick = useMobileBuyClick({
                mayPromo2025Enabled,
                isTariffsPage,
                onBuyClick,
            });

            // отправляем событие с периодом из фичи или раз в 10 минут
            const sendStartViewEvent = throttle(() => {
                dispatch(showLandingTariffsAction());
            }, sendStartEventTimer || START_EVENT_TIMER_MS);

            useEffect(() => {
                xray.send('quota_show_tariffs_list');

                if (Boolean(quotaLandingLoadProductsTimeout) && !isTariffsPage) {
                    xray.send(`quota_tariffs_timeout_init_mobile`);
                }

                return () => {
                    if (productsLoadTimer?.current) {
                        clearTimeout(productsLoadTimer.current);
                        productsLoadTimer.current = null;
                    }
                };
            }, []);

            useEffect(() => {
                // При переключении табов восстанавливаем позицию слайдера на первый элемент.
                if (swiperRef.current) {
                    swiperRef.current?.slideTo(0);
                }

                setCurrentSpace(withActionTariff ? SPACE_LIST.tb_1 : spaces[0]);
            }, [activeTariffTab, spaces.length, withActionTariff]);

            useEffect(() => {
                if (isLoaded && !firstLoad.current) {
                    firstLoad.current = true;

                    if (multiTariffSendTariffAnalytics) {
                        sendQuotaGa({
                            action: 'tariffs',
                            label: 'list',
                            tariffs: getMultiTariffsListMobileIds(products),
                        });
                    }
                }
            }, [isLoaded]);

            useEffect(() => {
                if (isLoaded && activeTariffTab && multiTariffHashTariffAnalytics) {
                    sendMultiTariffHashAnalytics(
                        { [activeTariffTab]: Object.values(products).map((productsMap) => ({ products: productsMap })) },
                        activeTariffTab,
                        tabOptions
                    );
                }
            }, [isLoaded, activeTariffTab]);

            const onIntersecting = useCallback(() => {
                if (sendStartEventTimer) {
                    sendStartViewEvent();
                }

                emitAnalyticEvent(AnalyticEventNames.TARIFFS_SHOWN, { isAlmostFullFreeUser });
                sendQuotaGa({
                    action: 'tariff',
                    label: 'view',
                    multi: true,
                    dwh_enabled: true,
                    dwh_enabled_fields: [['multi', 'tariffs_line', 'platform']],
                });
                sendTmrGoal({ id: TMR3473039, goal: 'tariff-view' });
                sendQuotaBlockViewDWH('tariffs');

                changeTab?.(ETabs.tariffs);

                /**
                 * Загружаем продукты если юзера доскролил до блока с тарифами и остался
                 * на нем какое-то время (quotaLandingLoadProductsTimeout)
                 */
                if (!isLoaded && !isLoading && !productsLoadTimer?.current && !isTariffsPage && quotaLandingLoadProductsTimeout) {
                    const id = setTimeout(() => {
                        xray.send('quota_tariffs_timeout_load');
                        dispatch(loadQuotaLandingProducts());
                    }, quotaLandingLoadProductsTimeout);

                    productsLoadTimer.current = id;
                }
            }, [isLoaded, isLoading, dispatch, isTariffsPage, changeTab]);

            const onIntersectingOut = useCallback(() => {
                if (productsLoadTimer?.current) {
                    /**
                     * Сбрасываем таймер загрузки продуктов
                     * Нужно чтобы продукты не грузились когда происходит быстрый якорный скролл.
                     */
                    clearTimeout(productsLoadTimer.current);
                    productsLoadTimer.current = null;
                }
            }, []);

            const refIntersection = useIntersecObsrWithParams({
                onIntersecting,
                onIntersectingOut,
                isMobile: true,
                headerHeight,
                threshold: quotaLandingLoadProductsThresholdMobile,
            });

            const handleSwiper = useCallback((swiper: SwiperInstance) => {
                swiperRef.current = swiper;
            }, []);

            const handleSwiperDestroy = useCallback(() => {
                swiperRef.current = null;
            }, []);

            const handleInfoIconClick = useCallback(() => {
                sendQuotaGa({ action: 'tooltip-family-option', label: 'info-click' });
                setShowTooltip((prev) => !prev);
            }, [setShowTooltip]);

            const handlePromocode = useCallback(() => {
                openPromocodeModal();
                sendPromocodeButtonAnalytics('click', 'tariffs', 'touch');
            }, []);

            const tariffCard = useMemo(() => {
                if (!selectorDataLoaded || !currentSpace || !isLoaded) {
                    return <MultiTariffCardLoader />;
                }

                return (
                    <>
                        <div className={styles.card}>
                            {showSpecialFamilyCard ? (
                                <div className={styles.cardFamily}>
                                    <Spacing size={12} />
                                    <div className={styles.familyTariff}>
                                        <div className={styles.value}>{bytesToNDigits(currentSpace, 3).space}</div>
                                        <div className={styles.units}>{bytesToNDigits(currentSpace, 3).units}</div>
                                    </div>
                                    <Spacing size={16} />
                                </div>
                            ) : (
                                <div className={styles.list}>
                                    <Swiper
                                        spaceBetween={12}
                                        slidesOffsetAfter={24}
                                        slidesOffsetBefore={24}
                                        slidesPerView={'auto'}
                                        onSwiper={handleSwiper}
                                        onDestroy={handleSwiperDestroy}
                                        className={classNames({
                                            [styles.delayed_animated_slider]: multiTariffsMobileTabsAnimation,
                                            [styles.animated_slider]: multiTariffTouchRegularOrAnonymous === 'b',
                                        })}
                                    >
                                        {spaces.map((space, index) => {
                                            return (
                                                <SwiperSlide
                                                    key={`${space}-${activeTariffTab}`}
                                                    virtualIndex={index}
                                                    className={styles.tariffSlide}
                                                >
                                                    <MultiTariffsListMobileSpaceCard
                                                        index={index}
                                                        currentSpace={currentSpace}
                                                        space={space}
                                                        activeTariffTab={activeTariffTab}
                                                        withActionTariff={withActionTariff}
                                                        yearProduct={yearProduct}
                                                        onClick={() => {
                                                            setCurrentSpace(space);
                                                        }}
                                                    />
                                                </SwiperSlide>
                                            );
                                        })}
                                    </Swiper>
                                </div>
                            )}
                            <Spacing size={20} />
                            <Features
                                space={currentSpace}
                                product={yearProduct}
                                onBuyClick={onMobileBuyClick}
                                activeTariffTab={activeTariffTab}
                                showDivider={familyByOptionsFeatureMobile && activeTariffTab === ETabsName.advanced}
                                blackFriday2025Enabled={blackFriday2025Enabled}
                            />
                            {familyByOptionsFeatureMobile && activeTariffTab === ETabsName.advanced && (
                                <>
                                    <div className={s['familyByOptions']}>
                                        <img src={familyByOptionImage} alt="Семья" className={s['familyByOptionsMedia']} />
                                        <span className={s['familyByOptionsText']}>
                                            Функции для семьи
                                            <div className={s['familyByOptionsIcon']}>
                                                <Icon20InfoCircleOutline
                                                    fill={theme.colorIconPrimary.normal.value}
                                                    width={20}
                                                    height={20}
                                                    onClick={handleInfoIconClick}
                                                />
                                            </div>
                                        </span>
                                        <span
                                            className={classNames(s['familyByOptionsAdditionalText'], {
                                                [s['familyByOptionsAdditionalTextChecked']]: familyByOptionsSwitchState,
                                            })}
                                        >
                                            {familyByOptionsSwitchState
                                                ? `${tariffPriceDif} ₽ в месяц`
                                                : `ещё +${tariffPriceDif} ₽ в месяц`}
                                        </span>

                                        <div className={s['familyByOptionSwitch']} ref={infoTooltipRef}>
                                            <Switch
                                                name="family-by-options"
                                                checked={familyByOptionsSwitchState}
                                                onChange={familyByOptionsHandler}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <Spacing size={16} />
                        {monthProduct && yearProduct && (
                            <BuyButtons
                                monthProduct={monthProduct}
                                yearProduct={yearProduct}
                                onBuy={onMobileBuyClick}
                                blackFriday2025Enabled={blackFriday2025Enabled}
                            />
                        )}
                    </>
                );
            }, [
                monthProduct,
                yearProduct,
                spaces,
                currentSpace,
                selectorDataLoaded,
                isLoaded,
                activeTariffTab,
                familyByOptionsSwitchState,
                familyTariffForCurrentSpace,
                withActionTariff,
            ]);

            const tooltip = showTooltip && (
                <FamilyByOptionTooltip ref={infoTooltipRef} onClose={() => setShowTooltip(false)} placement={ETooltipPlacement.topEnd} />
            );

            return (
                <div ref={refIntersection}>
                    <div ref={ref} data-qa-id="multi-tariffs-list-mobile">
                        <MultiTariffsTabsMobile
                            tabOptions={tabOptions}
                            activeTariffTab={activeTariffTab}
                            onChangeTab={(value) => {
                                dispatch(changeMultiTariffMobileActiveTabAction(value));
                                emitAnalyticEvent(AnalyticEventNames.MULTITARIFF_TYPE_CLICK_M);
                                sendQuotaGa({
                                    action: 'change-tariff-line',
                                    name_button_from: activeTariffTab,
                                    name_button_to: value,
                                });
                            }}
                        />
                        <Container isNarrow isMobile>
                            <Spacing size={20} />
                            {multiTariffVerticalMobile ? (
                                <>
                                    <div className={styles.featuresBlock}>
                                        <Features
                                            space={currentSpace}
                                            product={yearProduct}
                                            onBuyClick={onMobileBuyClick}
                                            activeTariffTab={activeTariffTab}
                                        />
                                    </div>
                                    <Spacing size={32} />
                                    <VerticalTariffCardList onBuy={onMobileBuyClick} />
                                </>
                            ) : (
                                tariffCard
                            )}
                            {promocodeInTariffsMobile && (
                                <>
                                    <Spacing size={12} />
                                    <Button
                                        theme="vk"
                                        sizeMode={ButtonSizeMode.extraBig}
                                        className={styles.buttonPromocodeMobile}
                                        onClick={handlePromocode}
                                    >
                                        Ввести промокод
                                    </Button>
                                </>
                            )}
                            {tooltip}
                        </Container>
                    </div>
                </div>
            );
        }
    )
);

MultiTariffsListMobile.displayName = 'MultiTariffsListMobile';
